/**
 * Agent/diagnostics detail — drill-down from the diagnostics list.
 *
 * Shows everything we know about one agent:
 *  - identity (hostname, OS, version, IP, registered at)
 *  - last signals (heartbeat, screenshot, activity)
 *  - recent activity rows + recent screenshot thumbnails
 *
 * If the agent is dead, the "App hiện tại" panel will show stale data
 * from the last successful activity log — useful for "was this NV even
 * on the right app when it died?". Auto-refreshes every 30s.
 */
import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Camera, Monitor } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { diagnosticsService } from '@/services/diagnostics.service'
import { formatRelativeTime } from '@/lib/format'
import { resolveScreenshotUrl } from '@/lib/axios'
import type { AgentDiagDetail } from '@monitor/shared'

export default function AgentDiagnosticsPage() {
  const { id } = useParams<{ id: string }>()
  const employeeId = Number(id)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['diagnostics', employeeId],
    queryFn: () => diagnosticsService.detail(employeeId),
    enabled: Number.isFinite(employeeId),
    refetchInterval: 30_000,
  })

  // Refetch when the screen toggles to foreground so the "Mất kết nối"
  // countdown is accurate without an explicit F5.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') refetch()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [refetch])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (!data) {
    return (
      <div className="container py-6">
        <p className="text-muted-foreground">Không tìm thấy NV.</p>
      </div>
    )
  }

  const online = data.isOnline
  const stale = online && (data.secsSinceLastSignal ?? 0) > 300

  const badgeVariant = stale ? 'warning' : online ? 'success' : 'secondary'
  const badgeText = stale ? 'Stale' : online ? 'Online' : 'Offline'

  return (
    <div className="container space-y-4 py-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/diagnostics"
            aria-label="Quay lại"
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold">
              {data.fullName} <span className="text-muted-foreground">({data.employeeCode})</span>
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant={badgeVariant}>{badgeText}</Badge>
              {data.isActive ? null : <Badge variant="destructive">Inactive</Badge>}
              <span className="text-xs text-muted-foreground">
                {data.department ?? '—'} {data.position ? `· ${data.position}` : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Identity + last signals */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Monitor className="h-4 w-4" />
              Máy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row k="Hostname" v={data.hostname ?? '—'} mono />
            <Row k="OS" v={data.osVersion ?? '—'} />
            <Row k="Version" v={data.agentVersion ?? '—'} />
            <Row k="IP" v={data.ipAddress ?? '—'} mono />
            <Row
              k="Registered"
              v={data.registeredAt ? formatRelativeTime(data.registeredAt) : '—'}
            />
          </CardContent>
        </Card>

        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Tín hiệu gần nhất</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row k="Heartbeat" v={data.lastHeartbeat ? formatRelativeTime(data.lastHeartbeat) : '—'} />
            <Row
              k="Screenshot"
              v={data.lastScreenshotAt ? formatRelativeTime(data.lastScreenshotAt) : '—'}
            />
            <Row k="Activity" v={data.lastActivityAt ? formatRelativeTime(data.lastActivityAt) : '—'} />
            <Row k="secsSinceLastSignal" v={data.secsSinceLastSignal?.toString() ?? '—'} />
            <Row k="24h screenshots" v={String(data.screenshotCount24h)} />
            <Row k="24h activity" v={String(data.activityCount24h)} />
          </CardContent>
        </Card>

        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">App hiện tại</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row k="Tên app" v={data.currentApp ?? '—'} />
            <Row k="Tiêu đề" v={data.currentTitle ?? '—'} />
            <Row k="URL" v={data.currentUrl ?? '—'} mono />
            <Row k="Idle" v={data.isIdle ? 'Có' : 'Không'} />
          </CardContent>
        </Card>
      </div>

      {/* Recent activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity gần nhất</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có activity log.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="p-2">Lúc</th>
                    <th className="p-2">App</th>
                    <th className="p-2">Tiêu đề</th>
                    <th className="p-2">URL</th>
                    <th className="p-2">Idle</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentActivity.map((a: AgentDiagDetail['recentActivity'][number]) => (
                    <tr key={a.id} className="border-b hover:bg-muted/40">
                      <td className="p-2 text-xs">{formatRelativeTime(a.loggedAt)}</td>
                      <td className="p-2">{a.appName ?? '—'}</td>
                      <td className="p-2 max-w-[260px] truncate" title={a.windowTitle ?? ''}>
                        {a.windowTitle ?? '—'}
                      </td>
                      <td className="p-2 max-w-[260px] truncate font-mono text-xs" title={a.url ?? ''}>
                        {a.url ?? '—'}
                      </td>
                      <td className="p-2">{a.isIdle ? 'Có' : 'Không'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent screenshots */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="h-4 w-4" />
            Screenshots gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentScreenshots.length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có screenshot.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {data.recentScreenshots.map((s: AgentDiagDetail['recentScreenshots'][number]) => (
                <a
                  key={s.id}
                  href={resolveScreenshotUrl(s.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-md border bg-muted"
                >
                  <img
                    src={resolveScreenshotUrl(s.url)}
                    alt={`Screenshot at ${s.capturedAt}`}
                    className="aspect-video w-full object-cover"
                    loading="lazy"
                  />
                  <div className="p-1 text-center text-xs text-muted-foreground">
                    {formatRelativeTime(s.capturedAt)}
                  </div>
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button variant="outline" onClick={() => refetch()}>
          Làm mới
        </Button>
      </div>
    </div>
  )
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? 'font-mono text-xs' : ''} title={v}>
        {v}
      </span>
    </div>
  )
}
